import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { useCampaign } from "@/contexts/CampaignContext";
import { PageContainer } from "@/components/PageContainer";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { DateRange } from "react-day-picker";
import { format } from "date-fns";
import { CalendarIcon } from "lucide-react";
import { CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { useToast } from "@/hooks/useToast";

const contentFocusOptions = [
  "All", "News", "Sports", "Home Improvement", "DIY", "Food",
  "Entertainment", "Nature/Science", "Travel", "Weather", "Crime",
  "Health & Wellness", "Reality TV", "History", "Movies"
];

const dayPartingOptions = [
  "All Times", "Weekend", "Weekdays", "Early Morning", "Evenings",
  "Late Night", "Working Hours", "School Hours", "Happy Hour"
];

export function CampaignDetails() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { campaignDetails, setCampaignDetails } = useCampaign();
  const form = useForm({
    defaultValues: campaignDetails || {
      clientName: "",
      campaignName: "",
      budget: 5000,
      cpm: 65,
      floorFrequency: 1,
      contentFocus: ["All", ...contentFocusOptions.slice(1)],
      dayParting: "All Times",
      notes: "",
      dateRange: {
        from: new Date(),
        to: new Date()
      }
    }
  });

  const { register, handleSubmit, watch, setValue, formState: { errors } } = form;

  const budget = watch("budget");
  const cpm = watch("cpm");
  const floorFrequency = watch("floorFrequency");
  const dateRange = watch("dateRange");
  const contentFocus = watch("contentFocus");

  const impressions = (budget / cpm) * 1000;
  const audienceReach = impressions / floorFrequency;

  const handleContentFocusChange = (option: string) => {
    const currentValues = form.getValues("contentFocus");
    let newValues: string[];

    if (option === "All") {
      newValues = currentValues.includes("All") ? [] : contentFocusOptions;
    } else {
      if (currentValues.includes(option)) {
        newValues = currentValues.filter(value => value !== option);
        newValues = newValues.filter(value => value !== "All");
      } else {
        newValues = [...currentValues, option];
        if (newValues.length === contentFocusOptions.length - 1) {
          newValues.push("All");
        }
      }
    }

    setValue("contentFocus", newValues);
  };

  const onSubmit = (data) => {
    if (data.budget < 2500) {
      toast({
        title: "Invalid Budget",
        description: "Campaign budget must be at least $2,500",
        variant: "destructive"
      });
      return;
    }

    if (data.cpm < 65) {
      toast({
        title: "Invalid CPM",
        description: "CPM must be at least 65",
        variant: "destructive"
      });
      return;
    }

    if (data.floorFrequency < 1 || data.floorFrequency > 10) {
      toast({
        title: "Invalid Floor Frequency",
        description: "Floor frequency must be between 1 and 10",
        variant: "destructive"
      });
      return;
    }

    const impressions = Math.floor((parseFloat(data.budget) / parseFloat(data.cpm)) * 1000);
    const audienceReach = Math.floor(impressions / parseFloat(data.floorFrequency));

    setCampaignDetails({
      ...data,
      startDate: data.dateRange.from,
      endDate: data.dateRange.to,
      impressions,
      audienceReach
    });
    navigate("/create/audience");
  };

  return (
    <PageContainer>
      <form onSubmit={handleSubmit(onSubmit)}>
        <CardHeader>
          <CardTitle>Campaign Details</CardTitle>
          <CardDescription>Enter your campaign information below</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label htmlFor="clientName">Client Name</Label>
              <Input {...register("clientName", { required: true })} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="campaignName">Campaign Name</Label>
              <Input {...register("campaignName", { required: true })} />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label htmlFor="budget">Campaign Budget ($)</Label>
              <Input
                type="number"
                {...register("budget", {
                  required: "Budget is required",
                  min: {
                    value: 2500,
                    message: "Budget must be at least $2,500"
                  }
                })}
              />
              {errors.budget && (
                <p className="text-sm text-red-500 mt-1">{errors.budget.message}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="cpm">CPM (Minimum 65)</Label>
              <Input
                type="number"
                {...register("cpm", {
                  required: "CPM is required",
                  min: {
                    value: 65,
                    message: "CPM must be at least 65"
                  }
                })}
              />
              {errors.cpm && (
                <p className="text-sm text-red-500 mt-1">{errors.cpm.message}</p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label>Impressions</Label>
              <Input value={impressions.toFixed(0)} disabled />
            </div>
            <div className="space-y-2">
              <Label htmlFor="floorFrequency">Floor Frequency (1-10)</Label>
              <Input
                type="number"
                {...register("floorFrequency", {
                  required: "Floor frequency is required",
                  min: {
                    value: 1,
                    message: "Floor frequency must be at least 1"
                  },
                  max: {
                    value: 10,
                    message: "Floor frequency cannot exceed 10"
                  }
                })}
              />
              {errors.floorFrequency && (
                <p className="text-sm text-red-500 mt-1">{errors.floorFrequency.message}</p>
              )}
            </div>
          </div>

          <div className="space-y-2">
            <Label>Audience Reach</Label>
            <Input value={audienceReach.toFixed(0)} disabled />
          </div>

          <div className="space-y-4">
            <Label>Campaign Duration</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    "w-full justify-start text-left font-normal",
                    !dateRange && "text-muted-foreground"
                  )}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {dateRange?.from ? (
                    dateRange.to ? (
                      <>
                        {format(dateRange.from, "LLL dd, y")} -{" "}
                        {format(dateRange.to, "LLL dd, y")}
                      </>
                    ) : (
                      format(dateRange.from, "LLL dd, y")
                    )
                  ) : (
                    <span>Pick a date range</span>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  initialFocus
                  mode="range"
                  defaultMonth={dateRange?.from}
                  selected={dateRange}
                  onSelect={(range: DateRange | undefined) => setValue("dateRange", range)}
                  numberOfMonths={2}
                />
              </PopoverContent>
            </Popover>
          </div>

          <div className="space-y-4">
            <Label>Content Focus</Label>
            <div className="flex flex-wrap gap-2">
              {contentFocusOptions.map((option) => (
                <button
                  key={option}
                  type="button"
                  onClick={() => handleContentFocusChange(option)}
                  className={cn(
                    "px-4 py-2 rounded-full border transition-colors",
                    contentFocus?.includes(option)
                      ? "bg-primary text-primary-foreground border-primary"
                      : "bg-background border-input hover:bg-accent hover:text-accent-foreground"
                  )}
                >
                  {option}
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-4">
            <Label>Day Parting</Label>
            <Select
              onValueChange={(value) => setValue("dayParting", value)}
              defaultValue={watch("dayParting")}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select day parting" />
              </SelectTrigger>
              <SelectContent>
                {dayPartingOptions.map((option) => (
                  <SelectItem key={option} value={option}>
                    {option}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="notes">Notes/Special Requests</Label>
            <Textarea {...register("notes")} />
          </div>
        </CardContent>
        <CardFooter className="flex justify-end space-x-4">
          <Button type="submit">Next: Audience Targeting</Button>
        </CardFooter>
      </form>
    </PageContainer>
  );
}